import { type OptionsType } from '@atlaskit/select';
import {
	type AlertDetailDynamicMessage,
	type AlertDetailFeedbackMessage,
	AlertDetailHandler,
	type DynamicDetailView,
} from '@atlassian/eoc-alert-detail';
import { type GlobalMessageHandler } from '@atlassian/eoc-global-context';
import {
	STRING_LITERAL_STATUS_ACKED,
	STRING_LITERAL_STATUS_CLOSED,
	STRING_LITERAL_STATUS_OPEN,
	STRING_LITERAL_STATUS_SNOOZED,
} from '@atlassian/eoc-query-editor';

import {
	type AlertListDynamicFieldMessage,
	type AlertListDynamicFilterMessage,
	type AlertListDynamicObjectMessage,
	type AlertListDynamicTableCellMessage,
	type AlertListFeedbackMessage,
	type AlertListMessage,
	type MoreOption,
	type QueryDynamicItemsById,
	type QueryDynamicItemsByName,
} from '../types';

export const MORE_OPTIONS: OptionsType<MoreOption> = [
	{
		label: 'Assignee',
		value: 'owner',
	},
	{
		label: 'Deduplication',
		value: 'count',
	},
	{
		label: 'Tiny ID',
		value: 'tinyId',
	},
	{
		label: 'Tags',
		value: 'tags',
	},
	{
		label: 'Source',
		value: 'source',
	},
	{
		label: 'Entity',
		value: 'entity',
	},
	{
		label: 'Alias',
		value: 'alias',
	},
];

export const STATUS_OPTIONS = {
	[STRING_LITERAL_STATUS_OPEN]: {
		label: 'Open',
		appearance: 'removed',
		displayValue: 'open',
	},
	[STRING_LITERAL_STATUS_ACKED]: {
		label: 'Acknowledged',
		appearance: 'inprogress',
		displayValue: 'acked',
	},
	[STRING_LITERAL_STATUS_SNOOZED]: {
		label: 'Snoozed',
		appearance: 'moved',
		displayValue: 'snoozed',
	},
	[STRING_LITERAL_STATUS_CLOSED]: {
		label: 'Closed',
		appearance: 'default',
		lozengeAppearance: 'default',
		displayValue: 'closed',
	},
} as const;

export const OPERATOR_OPTIONS = [
	{
		label: '<=',
		value: '<=',
	},
	{
		label: '<',
		value: '<',
	},
	{
		label: '=',
		value: '=',
	},
	{
		label: '>=',
		value: '>=',
	},
	{
		label: '>',
		value: '>',
	},
];

export const EMPTY_STATE_SVG_WIDTH = 167;
export const ERROR_STATE_SVG_WIDTH = 226;
export const NO_ALERTS_STATE_SVG_WIDTH = 160;
export const EMPTY_STATE_SVG_HEIGHT = 146;
export const ERROR_STATE_SVG_HEIGHT = 210;
export const NO_ALERTS_STATE_SVG_HEIGHT = 136;

export const SPOTLIGHT_NAMES = {
	filters: 'eoc-alert-list_filters',
	myAlerts: 'eoc-alert-list_my-alerts',
	actions: 'eoc-alert-list_actions',
	sidebarItems: 'eoc-alert-list_sidebar_items',
	headerActions: 'eoc-alert-list_header-actions',
	aiBanner: 'eoc-alert-list_ai-banner',
	aiView: 'eoc-alert-list_ai-view',
	aiGroupCountBadge: 'eoc-alert-list_ai-group-count-badge',
	responders: 'eoc-alert-list_responders',
};

export type DynamicFilterType = {
	getFormatOptionLabel: (option: any) => React.ReactNode;
	placeholder: string;
	label: string;
	queryDynamicItemsByName: QueryDynamicItemsByName;
	queryDynamicItemsById: QueryDynamicItemsById;
};
export type DynamicFieldType = {
	label: string;
	requestTarget: string;
	queryDynamicItemsByName: QueryDynamicItemsByName;
	queryDynamicItemsById: QueryDynamicItemsById;
};
export type ServiceItem = {
	label: string;
	value: string;
	type: string;
	icon: JSX.Element;
};

export class AlertListHandler extends AlertDetailHandler implements GlobalMessageHandler<unknown> {
	handler(message: AlertDetailFeedbackMessage, context?: any): void;
	handler(message: AlertDetailDynamicMessage, context?: any): { [key: string]: DynamicDetailView };
	handler(message: AlertListFeedbackMessage, context?: unknown): void;
	handler(
		message: AlertListDynamicObjectMessage,
		context?: any,
	): (query: string[]) => Promise<ServiceItem[] | void>;
	handler(
		message: AlertListDynamicTableCellMessage,
		context?: any,
	): {
		header: { key: string; className: string; content: () => JSX.Element };
		cell: { key: string; className: string; content: () => JSX.Element };
	}[];
	handler(
		message: AlertListDynamicFilterMessage,
		context?: any,
	): { [key: string]: DynamicFilterType };
	handler(message: AlertListDynamicFieldMessage, context?: any): DynamicFieldType;

	handler(message: AlertListMessage): string | object[] | object {
		throw new Error('Not implemented');
	}
}
